import { Telemetry } from '@atlas/observability';
import { catchToResult, throwOnErr } from '@core/util/result';
import { matchesTokenSubsequences } from '@core/util/string';
import { queryClient } from '@queries/client';
import { queryReadyGate } from '@queries/gate';
import { useCurrentTeamQuery } from '@queries/team/teams';
import type { DocumentNoteLink as CalendarDocumentLink } from '@service-calendar/generated/schemas/documentNoteLink';
import { calendarNotesClient } from '@service-calendar/notes';
import { createDebouncedMemo } from '@solid-primitives/memo';
import { useMutation, useQuery } from '@tanstack/solid-query';
import { err, ok } from 'neverthrow';
import { type Accessor, createMemo } from 'solid-js';
import type {
  DocumentNotesSource,
  NoteDocumentSearch,
  NotesSource,
} from '../context/notes-source';
import type {
  DocumentNoteLink,
  MeetingNote,
  NoteCreateFailure,
  NoteFailure,
  NotePicker,
  NoteTarget,
} from '../core/types';
import { notesKeys } from './keys';

function notesOptions(userId: string | undefined, target: NoteTarget) {
  return {
    queryKey: notesKeys.notes(userId, target).queryKey,
    queryFn: async ({
      signal,
    }: {
      signal: AbortSignal;
    }): Promise<MeetingNote[]> => {
      const notes = await throwOnErr(() =>
        calendarNotesClient.list(target.eventId, target.occurrenceKey, signal)
      );
      return notes.map((note) => ({ ...note, team: note.team ?? undefined }));
    },
    enabled: !!userId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  };
}

function pickerOptions(
  userId: string | undefined,
  query: string,
  enabled: boolean,
  eventId: string,
  localCount: number
) {
  return {
    queryKey: notesKeys.documents(userId, query).queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      Telemetry.span('calendar.notes.search', async (span) => {
        span.setAttr('event.id', eventId);
        span.setAttr('search.query_length', query.length);
        span.setAttr('search.debounce_ms', 180);
        span.setAttr('search.local_result_count', localCount);
        const result = await span.span(
          'calendar.notes.search.server',
          async (server) => {
            const result = await calendarNotesClient.documents(query, signal);
            if (result.isOk()) {
              server.setAttr(
                'search.result_count',
                result.value.documents.length
              );
            } else if (!signal.aborted) {
              server.error('Meeting note search failed');
            }
            return result;
          }
        );
        span.setAttr(
          'search.outcome',
          signal.aborted ? 'cancelled' : result.isOk() ? 'success' : 'error'
        );
        if (result.isErr() && !signal.aborted)
          span.error('Meeting note search failed');
        return throwOnErr(async () => result);
      }),
    enabled: !!userId && enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    // Keep matching results while typing, but never carry them across accounts.
    placeholderData: (
      previous: NotePicker | undefined,
      previousQuery: { queryKey: readonly unknown[] } | undefined
    ) => (previousQuery?.queryKey[2] === userId ? previous : undefined),
  };
}

type NoteWrite = NoteTarget & { documentId: string; teamId?: string };

function linkNote(input: NoteWrite) {
  return Telemetry.span('calendar.notes.link', async (span) => {
    span.setAttr('event.id', input.eventId);
    span.setAttr('document.id', input.documentId);
    await throwOnErr(() =>
      calendarNotesClient.link(
        input.eventId,
        input.occurrenceKey,
        input.documentId,
        { teamId: input.teamId ?? null }
      )
    );
  });
}

function unlinkNote(input: NoteWrite) {
  return Telemetry.span('calendar.notes.unlink', async (span) => {
    span.setAttr('event.id', input.eventId);
    span.setAttr('document.id', input.documentId);
    await throwOnErr(() =>
      calendarNotesClient.unlink(
        input.eventId,
        input.occurrenceKey,
        input.documentId
      )
    );
  });
}

function refreshNotes() {
  // The same meeting can be open through several inbox copies with distinct IDs.
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: notesKeys.notes._def }),
    queryClient.invalidateQueries({ queryKey: notesKeys.documentLinks._def }),
  ]);
}

export function createNotesSource(
  userId: Accessor<string | undefined>,
  target: Accessor<NoteTarget>,
  localSearch: NoteDocumentSearch
): NotesSource {
  const notes = useQuery(() => notesOptions(userId(), target()));
  const link = useMutation(() => ({
    mutationFn: linkNote,
    onSuccess: refreshNotes,
  }));
  const unlink = useMutation(() => ({
    mutationFn: unlinkNote,
    onSuccess: refreshNotes,
  }));
  return {
    notes: () => ({
      data: queryReadyGate(notes) ? notes.data : undefined,
      pending: notes.isPending,
      error: notes.isError ? 'Couldn’t load meeting notes.' : undefined,
    }),
    async create(name) {
      // Capture the occurrence and account before document creation yields.
      const destination = { ...target() };
      const owner = userId();
      return Telemetry.span('calendar.notes.create', async (span) => {
        span.setAttr('event.id', destination.eventId);
        const created = await catchToResult(() =>
          createMarkdownFile({
            title: name,
            source: 'calendar_notes',
          })
        );
        if (created.isErr() || !created.value) {
          span.setAttr('calendar.notes.outcome', 'create_failed');
          span.error('Meeting note creation failed');
          return err({
            kind: 'create_failed',
            message: 'Couldn’t create the note. Please try again.',
          } satisfies NoteCreateFailure);
        }
        const document = { documentId: created.value, name };
        span.setAttr('document.id', document.documentId);
        if (userId() !== owner) {
          span.setAttr('calendar.notes.outcome', 'account_changed');
          span.error('Account changed before meeting note linking');
          return err({
            kind: 'created_unlinked',
            document,
            message:
              'Your note was saved, but your account changed before it could be linked.',
          } satisfies NoteCreateFailure);
        }
        const linked = await catchToResult(() =>
          link.mutateAsync({ ...destination, documentId: document.documentId })
        );
        if (linked.isErr()) {
          span.setAttr('calendar.notes.outcome', 'created_unlinked');
          span.error('Created meeting note could not be linked');
          return err({
            kind: 'created_unlinked',
            document,
            message:
              'Your note was saved, but couldn’t be linked to this meeting. Retry linking or open the saved note.',
          } satisfies NoteCreateFailure);
        }
        span.setAttr('calendar.notes.outcome', 'linked');
        return ok(document);
      });
    },
    picker(query, enabled) {
      const normalized = () => query().trim();
      const debounced = createDebouncedMemo(normalized, 180);
      const local = localSearch(normalized, enabled);
      const picker = useQuery(() =>
        pickerOptions(
          userId(),
          debounced(),
          enabled(),
          target().eventId,
          local().length
        )
      );
      const data = createMemo(() => {
        const remote = queryReadyGate(picker) ? picker.data : undefined;
        const documents = new Map(
          local().map((document) => [document.documentId, document])
        );
        for (const document of remote?.documents ?? []) {
          if (matchesTokenSubsequences(document.name, normalized())) {
            documents.set(document.documentId, document);
          }
        }
        return {
          documents: [...documents.values()],
          teams: remote?.teams ?? [],
        };
      });
      return () => ({
        data: data(),
        pending: picker.isFetching || normalized() !== debounced(),
        error: picker.isError
          ? 'Couldn’t load your notes. Try again.'
          : undefined,
      });
    },
    retry: () => notes.refetch(),
    async link(documentId, teamId) {
      const input = { ...target(), documentId, teamId };
      return (await catchToResult(() => link.mutateAsync(input))).mapErr(
        (): NoteFailure => ({
          kind: 'link_failed',
          message:
            'Couldn’t save this link. Check that you still own the note and can view the meeting, then try again.',
        })
      );
    },
    async unlink(documentId) {
      const input = { ...target(), documentId };
      return (await catchToResult(() => unlink.mutateAsync(input))).mapErr(
        (): NoteFailure => ({
          kind: 'unlink_failed',
          message:
            'Couldn’t unlink this note. Your document is safe. Please try again.',
        })
      );
    },
  };
}

/** Reference previews are adapted at the calendar API boundary. */
export type DocumentReference = DocumentNoteLink &
  Partial<Pick<CalendarDocumentLink, 'teamEvent' | 'event' | 'canViewMeeting'>>;

export function documentLinksOptions(
  userId: string | undefined,
  documentId: string
) {
  return {
    queryKey: notesKeys.documentLinks(userId, documentId).queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const links = await throwOnErr(() =>
        calendarNotesClient.documentLinks(documentId, signal)
      );
      return links.map((link) => ({ ...link, team: link.team ?? undefined }));
    },
    enabled: !!userId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  };
}

export function createDocumentNotesSource(
  userId: Accessor<string | undefined>,
  documentId: Accessor<string>
): DocumentNotesSource {
  const links = useQuery(() => documentLinksOptions(userId(), documentId()));
  const team = useCurrentTeamQuery();
  const link = useMutation(() => ({
    mutationFn: linkNote,
    onSuccess: refreshNotes,
  }));
  const unlink = useMutation(() => ({
    mutationFn: unlinkNote,
    onSuccess: refreshNotes,
  }));
  return {
    links: () => ({
      data: queryReadyGate(links) ? links.data : undefined,
      pending: links.isPending,
      error: links.isError ? 'Couldn’t load meeting access.' : undefined,
    }),
    team: () => ({
      data: queryReadyGate(team) ? (team.data?.team ?? null) : undefined,
      pending: team.isPending,
      error: team.isError ? 'Couldn’t load your team.' : undefined,
    }),
    retry: () => Promise.all([links.refetch(), team.refetch()]),
    async setTeam(target, teamId) {
      const input = { ...target, documentId: documentId(), teamId };
      return (await catchToResult(() => link.mutateAsync(input))).mapErr(
        (): NoteFailure => ({
          kind: 'link_failed',
          message:
            'Couldn’t update team access. Check that you can still view the meeting, then try again.',
        })
      );
    },
    async unlink(target) {
      return (
        await catchToResult(() =>
          unlink.mutateAsync({ ...target, documentId: documentId() })
        )
      ).mapErr(
        (): NoteFailure => ({
          kind: 'unlink_failed',
          message: 'Couldn’t remove this meeting link. Please try again.',
        })
      );
    },
  };
}

import { createMarkdownFile } from '@core/util/create';
